import { faUndo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { selectState as selectStateAuth } from "authentication/main.slice";
import Loader from "components/Loader/Loader";
import DateSelector from "Dashboard/components/DateSelector";
import { useAppDispatch } from "hooks/hooks";
import { useCallback, useEffect, useMemo } from "react";
import {
  getCharges,
  requestDeletingCharge,
  selectState,
  setDateRange,
  setDischarging,
  setResourceChargePopoverState,
  setResourceDischargePopover,
  setResourceUpdateChargePopoverState,
  setUpdating,
  updateCharge,
  updateChargeTemporarily,
} from "../charges.slice";
import {
  getResource,
  selectResourceDetails,
} from "../../Details/details.slice";
import ResourceChargePopover from "./ResourceChargePopover";

import Popover from "components/Popover/Popover";
import InfoTooltip from "components/Popup/InfoTooltip";
import ToolboxButton, { ToolboxButtonIcon } from "Dashboard/components/Buttons/ToolboxButton";
import { ICharge } from "Dashboard/scenes/Resources/domain/ICharge";
import { IUpdateCharge } from "Dashboard/scenes/Resources/domain/IUpdateCharge";
import { RESOURCES_MODULE_NAME } from "Dashboard/scenes/Resources/domain/ResourcesModule";
import { haveAverageConsumption, unitNameLabel } from "Dashboard/scenes/Resources/helpers/resourceUtil";
import { debounce } from "lodash";
import DatePicker from "react-datepicker";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { hasFullAccessTo, hasWriteAccessTo } from "utils/authUtil";
import { FormatDateIso, FormatDateShort } from "utils/dateUtils";
import { FormatNumberOrNull, FormatNumberWithUnitOrNull, getAdjustedFormatForUnit } from "utils/numberUtils";
import NumberInput from "Dashboard/components/NumberInput";
import EmployeeSelect from "Dashboard/scenes/Resources/components/EmployeeSelect";
import { IOption } from "Dashboard/domain/IOption";
import { styles as selectStyles } from "Dashboard/components/SelectStyle";
import ProjectSelect from "Dashboard/scenes/Projects/components/ProjectSelect";
import { IProjectOption } from "Dashboard/domain/IProjectOption";
import ResourceUpdateChargePopover from "./ResourceUpdateChargePopover";
import DeleteChargeModal from "./DeleteChargeModal";

interface IProps {
  resourceId: number;
  employeeOptions: IOption[];
  projectOptions: IProjectOption[];
}

const ResourceCharges = ({ resourceId, employeeOptions, projectOptions }: IProps) => {
  const dispatch = useAppDispatch();

  const {
    loading,
    dateFrom,
    dateTo,
    charges,
    totalHours,
    totalKilometers,
    totalLiters,
    chargingResourcePopover,
    updateChargePopover,
  } = useSelector(selectState);
  const { user } = useSelector(selectStateAuth);
  const resourceDetails = useSelector(selectResourceDetails);
  const isResourceActive = resourceDetails?.resource.status === "ACTIVE"

  const selectDate = useCallback((data: { dateFrom: Date; dateTo: Date }) => {
    dispatch(setDateRange(data));
  }, [dispatch]);

  const loadCharges = useCallback(() => {
    if (dateFrom != null && dateTo != null && resourceId) {
      dispatch(
        getCharges({
          resourceId,
          dateFrom: FormatDateIso(dateFrom),
          dateTo: FormatDateIso(dateTo),
        })
      );
    }
  }, [dateFrom, dateTo, dispatch, resourceId]);

  useEffect(() => {
    loadCharges();
  }, [resourceId, dateFrom, loadCharges]);

  const openResourceDischargePopover = (
    charge: ICharge, event: React.MouseEvent<HTMLSpanElement, MouseEvent>
  ) => {
    dispatch(setDischarging(charge));
    dispatch(setResourceDischargePopover({
      isOpen: true,
      top: event.clientY,
      left: event.clientX,
    }));
  };

  const onChargeDeleted = useCallback(() => {
    loadCharges();
    if (resourceId) {
      dispatch(getResource(resourceId));
    }
  }, [dispatch, loadCharges, resourceId]);

  const getUsageDuration = (charge: ICharge) => {
    if (charge.usageAmountAtEnd === null) {
      return null;
    }
    return charge.usageAmountAtEnd - charge.usageAmountAtStart;
  };

  const onChangeBasicField = (data: IUpdateCharge) => {
    dispatch(updateChargeTemporarily(data));
    updateBasicFieldWithDelay(data);
  };

  const updateBasicFieldWithDelay = useMemo(
    () => debounce(async (data: IUpdateCharge) => {
      try {
        await dispatch(updateCharge(data)).unwrap();
        toast.success("Izmena je uspešno zabeležena.");
      } catch (response: any) {
        if (response.status === 406) {
          if (response.data.message !== undefined) {
            if (response.data.message === "Charge date cannot be after discharge date"
              || response.data.message === "Discharge date cannot be before charge date") {
              toast.error("Datum zaduženja ne sme biti posle datuma razduženja");
              return;
            } else if (response.data.message === "Charge date cannot be after tomorrow"
              || response.data.message === "Discharge date cannot be after tomorrow") {
              toast.error("Datum ne sme biti posle sutrašnjeg dana");
              return;
            } else if (response.data.message === "Changing discharge date is not allowed before doing discharge") {
              toast.error("Nije dozvoljena izmena datuma razduženja dok zaduženje nije razduženo");
              return;
            } else if (response.data.message === "Usage amount at start cannot be greater than usage amount at end"
              || response.data.message === "Usage amount at end cannot be lower than usage amount at start") {
              toast.error(`${resourceDetails?.resource.usageUnit.unitId === 'H' ? 'Sati' : 'Kilometraža'} na početku zaduženja ne smeju biti veći nego na kraju`);
              return;
            } else if (response.data.message === "Changing usage amount at end is not allowed before doing discharge") {
              toast.error(`Nije dozvoljena izmena ${resourceDetails?.resource.usageUnit.unitId === 'H' ? 'sati' : 'kilometara'} na kraju zaduženja dok zaduženje nije razduženo`);
              return;
            } else if (response.data.message.startsWith("Command requests updating charge with illegal quantity")) {
              toast.error("Nova količina ne može biti prihvaćena");
              return;
            }
          }
        }
        toast.error("Neočekivana greška");
      }
    }, 1000),
    [dispatch, resourceDetails?.resource?.usageUnit?.unitId]
  );

  return (
    <div className="col-2 loading-container">
      {(!resourceId || loading) && <Loader />}
      <div className="resources-box-header">
        <ToolboxButton
          icon={ToolboxButtonIcon.Add}
          onClick={hasWriteAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive ? (e) =>
            dispatch(
              setResourceChargePopoverState({
                isOpen: true,
                top: e.clientY,
                left: e.clientX,
              })
            )
            : undefined
          }
        />

        <span className="title">Zaduženje resursa</span>
        <span></span>
      </div>
      <div className="resources-box-sub-header">
        <span>Zadužio / mesto</span>
        <DateSelector
          selectDate={selectDate}
          showMonth={false} />
      </div>
      <div className="resources-box-list">
        <div className="resource-wrapp">
          {charges.map((charge, index) => (
            <div className="firstResource" key={index}>
              {hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive
                ? (
                  <span className="charge-employee hoverBorder">
                    <EmployeeSelect
                      customStyles={selectStyles.dropdownInDetails}
                      defaultValue={charge.employee.employeeId}
                      options={employeeOptions}
                      onChange={(employeeId: number) => {
                        onChangeBasicField({ resourceId: charge.resource.resourceId, chargeId: charge.chargeId, employeeId: employeeId })
                      }}
                    />
                  </span>
                )
                : (<span className="charge-employee">{charge.employee.name}</span>)}
              {charge.dateDischarge
                ? (<InfoTooltip tooltip="najam" classesProp="charge-lease-cost" element="span">{FormatNumberWithUnitOrNull(charge.leaseCost, 'rsd')}</InfoTooltip>)
                : (<div className="buttons-wrapper">
                  {charge.deletable && hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive
                    ? (<ToolboxButton
                      icon={ToolboxButtonIcon.Delete}
                      tooltip="Obriši"
                      noCircle={true}
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch(requestDeletingCharge(charge));
                      }}
                    />) : undefined}
                  <span
                    className={hasWriteAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive ? "button" : "button button-disabled"}
                    onClick={(e) => hasWriteAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive
                      ? openResourceDischargePopover(charge, e)
                      : {}
                    }
                  >
                    <FontAwesomeIcon className="fas" icon={faUndo} />
                  </span>
                </div>)}
              {hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive ?
                (
                  <span className="charge-project hoverBorder">
                    <ProjectSelect
                      options={projectOptions}
                      onChange={(projectId: number) => {
                        onChangeBasicField({ resourceId: charge.resource.resourceId, chargeId: charge.chargeId, projectId: projectId })
                      }}
                      defaultValue={charge.project.projectId}
                      customStyles={selectStyles.dropdownInDetails}
                    />
                  </span>

                )
                : (<span className="charge-project">{charge.project.name}</span>)}
              <span className={`charge-date-charge${hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive ? ' hoverBorder' : ''}`}>
                {hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive
                  ? (<DatePicker
                    onChange={(date) =>
                      onChangeBasicField({ resourceId: charge.resource.resourceId, chargeId: charge.chargeId, dateCharge: date ? date.toISOString().split("T")[0] : undefined })
                    }
                    showMonthDropdown
                    showYearDropdown
                    dateFormat="dd.MM.yy"
                    selected={charge.dateCharge ? new Date(charge.dateCharge) : null}
                    className="charge-date-charge"
                  />)
                  : FormatDateShort(charge.dateCharge)
                }
              </span>
              <span className={`charge-date-discharge${charge.dateDischarge && hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive ? ' hoverBorder' : ''}`}>
                {charge.dateDischarge && hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive
                  ? (<DatePicker
                    onChange={(date) =>
                      onChangeBasicField({ resourceId: charge.resource.resourceId, chargeId: charge.chargeId, dateDischarge: date ? date.toISOString().split("T")[0] : undefined })
                    }
                    showMonthDropdown
                    showYearDropdown
                    dateFormat="dd.MM.yy"
                    selected={new Date(charge.dateDischarge)}
                    className="charge-date-discharge"
                  />)
                  : FormatDateShort(charge.dateDischarge)
                }
              </span>
              <span className="charge-unit-duration">{unitNameLabel(charge.usageUnit)} {FormatNumberOrNull(getUsageDuration(charge), getAdjustedFormatForUnit(charge.usageUnit?.name))}</span>
              {resourceDetails && haveAverageConsumption(resourceDetails.resource.category)
                ? (
                  <InfoTooltip element="span"
                    tooltip={`Zadužen na ${FormatNumberWithUnitOrNull(charge.usageAmountAtStart, charge.usageUnit?.name)}, ${charge.usageAmountAtEnd ? `razdužen na ${FormatNumberWithUnitOrNull(charge.usageAmountAtEnd, charge.usageUnit?.name)}` : 'nije razdužen'}`}
                    classesProp={`charge-usage${hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive ? ' hoverBorder' : ''}`}
                    onClick={hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive
                      ? (e) => {
                        dispatch(setUpdating(charge));
                        dispatch(setResourceUpdateChargePopoverState({ isOpen: true, top: e.clientY, left: e.clientX }));
                      }
                      : undefined
                    }
                  >
                    {FormatNumberOrNull(charge.usageAmountAtStart, getAdjustedFormatForUnit(charge.usageUnit?.name))}
                  </InfoTooltip>
                )
                : (<span className="charge-usage">{FormatNumberOrNull(charge.usageAmountAtStart, getAdjustedFormatForUnit(charge.usageUnit?.name))}</span>)}

              {resourceDetails && !haveAverageConsumption(resourceDetails.resource.category) &&
                (hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive
                  ? (<InfoTooltip tooltip="izdata količina" classesProp="charge-quantity hoverBorder" element="span">
                    <NumberInput
                      formatAs="quantity"
                      className="charge-quantity"
                      value={`${charge.quantityAmount}`}
                      onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                        if (parsedValue !== charge.quantityAmount) {
                          onChangeBasicField({ resourceId: charge.resource.resourceId, chargeId: charge.chargeId, quantityAmount: parsedValue })
                        }
                      }}
                    />
                  </InfoTooltip>)
                  : (<InfoTooltip tooltip="izdata količina" classesProp="charge-quantity" element="span">{FormatNumberOrNull(charge.quantityAmount)}</InfoTooltip>)
                )
              }

              <InfoTooltip tooltip={charge.note} classesProp={`charge-note${hasWriteAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive ? ' hoverBorder' : ''}`} element="span">
                <input className="charge-note"
                  value={charge.note || ""}
                  onChange={(e) => {
                    onChangeBasicField({ resourceId: charge.resource.resourceId, chargeId: charge.chargeId, note: e.target.value })
                  }}
                  disabled={!(hasWriteAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive)}
                />
              </InfoTooltip>
              {hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive
                ?
                (<InfoTooltip tooltip="broj izveštaja" classesProp="charge-report-sequence hoverBorder" element="span">
                  <NumberInput
                    formatAs="quantity"
                    className="charge-report-sequence"
                    value={`${charge.chargeReportSequence}`}
                    onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                      if (parsedValue !== charge.chargeReportSequence) {
                        onChangeBasicField({ resourceId: charge.resource.resourceId, chargeId: charge.chargeId, chargeReportSequence: parsedValue })
                      }
                    }}
                  />
                </InfoTooltip>)
                :
                (<InfoTooltip tooltip="broj izveštaja" classesProp="charge-report-sequence" element="span">{FormatNumberOrNull(charge.chargeReportSequence)}</InfoTooltip>)}
            </div>
          ))}
          {charges.length < 4 && <div className="firstResource"></div>}
          {charges.length < 3 && <div className="firstResource"></div>}
          {charges.length < 2 && <div className="firstResource"></div>}
          {charges.length < 1 && <div className="firstResource"></div>}
        </div>
      </div>
      <div className="resources-box-footer">
        <span>{FormatNumberWithUnitOrNull(totalKilometers, 'km')}</span>
        <span>{FormatNumberWithUnitOrNull(totalHours, 'sati')}</span>
        <span>{FormatNumberWithUnitOrNull(totalLiters, 'litara')}</span>
      </div>
      <Popover
        isOpen={chargingResourcePopover.isOpen}
        onClose={() => {
          dispatch(setResourceChargePopoverState({ isOpen: false }));
        }}
        title="Zaduženje resursa"
        direction="right"
        arrowStyle={{ top: "50%" }}
        position={{
          top: -585,
          left: 460,
          offsetByWindowSizeY: true
        }}
      >
        <ResourceChargePopover onCharge={loadCharges} employees={employeeOptions} />
      </Popover>
      <Popover
        isOpen={updateChargePopover.isOpen}
        onClose={() => {
          dispatch(setResourceUpdateChargePopoverState({ isOpen: false }));
        }}
        title="Izmena zaduženja"
        direction="right"
        arrowStyle={{ top: "50px" }}
        position={{
          top: (updateChargePopover.top ?? 0) - 60,
          left: 485,
        }}
      >
        <ResourceUpdateChargePopover />
      </Popover>
      <DeleteChargeModal onDeleted={onChargeDeleted} />
    </div >
  );
};

export default ResourceCharges;
